import type { FormatPrefs } from '@trips/shared/format';
import { formatTime } from '@trips/shared/format';
import type { FlightLive } from '@trips/shared/schema';
import { Icon } from '../../ui/Icon';
import { StatusPill } from '../../ui/StatusPill';
import s from './YourPlane.module.css';

export type Inbound = NonNullable<FlightLive['inbound']>;

/** "DL1840" as "DL 1840". */
export const formatIdent = (ident: string) => ident.replace(/^([A-Z0-9]{2})(\d)/, '$1 $2');

/** Where the plane is in a line: leaving, flying, landed or at the gate. */
export function inboundStatus(inbound: Inbound, here: string, tz: string, prefs: FormatPrefs) {
  const at = (t: number | null | undefined) => (t != null ? formatTime(t, tz, prefs) : null);
  if (inbound.atGate)
    return `At the gate in ${here}${at(inbound.estimatedIn) ? `, got in ${at(inbound.estimatedIn)}` : ''}`;
  if (inbound.landed) return `Landed in ${here}, heading to the gate`;
  if (inbound.departed)
    return `In the air${inbound.origin ? ` from ${inbound.origin}` : ''}, lands ${at(inbound.estimatedIn) ?? 'soon'}`;
  return `Still ${inbound.origin ? `in ${inbound.origin}` : 'on the ground'}${inbound.departAt ? `, leaves ${at(inbound.departAt)}` : ''}`;
}

/**
 * Where the plane for my flight is before I board (byAir's "where's my plane"): the flight
 * it's on now, whether it has left, is flying, has landed or is at the gate, and when it gets in.
 */
export function YourPlane({
  inbound,
  here,
  tz,
  prefs,
  now,
  onMap,
}: {
  inbound: Inbound;
  /** My departure airport, where the plane is heading. */
  here: string;
  tz: string;
  prefs: FormatPrefs;
  now: number;
  /** Shows it on the map, when we know where it's coming from. */
  onMap?: () => void;
}) {
  const late = inbound.delayMinutes ?? 0;
  const flying = inbound.departed && !inbound.landed;
  const progress =
    flying && inbound.departAt != null && inbound.estimatedIn != null
      ? Math.min(
          1,
          Math.max(
            0,
            (now - inbound.departAt) / Math.max(1, inbound.estimatedIn - inbound.departAt),
          ),
        )
      : null;

  return (
    <section className={s.card} aria-label="Your plane">
      <div className={s.head}>
        <span className={s.title}>Your plane</span>
        <span className={s.side}>
          {late >= 15 ? (
            <StatusPill label={`${late} min late`} tone={late >= 60 ? 'bad' : 'late'} />
          ) : inbound.departed || inbound.atGate ? (
            <StatusPill label="On time" tone="ok" />
          ) : null}
          {onMap && (
            <button type="button" className={s.map} onClick={onMap}>
              <Icon name="map-trifold" size={16} />
              Map
            </button>
          )}
        </span>
      </div>
      <p className={s.status}>{inboundStatus(inbound, here, tz, prefs)}</p>
      {inbound.ident && <p className={`${s.sub} num`}>Flying in as {formatIdent(inbound.ident)}</p>}
      {progress != null && (
        <div className={s.track} aria-hidden="true">
          <span className={`${s.code} num`}>{inbound.origin}</span>
          <span className={s.bar}>
            <span style={{ width: `${Math.round(progress * 100)}%` }} />
          </span>
          <span className={`${s.code} num`}>{here}</span>
        </div>
      )}
    </section>
  );
}
